<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import FocalImage from '@/Components/FocalImage.vue'

interface ImageEntry {
    id: number
    name: string
    thumb: string
    large: string
    original: string
    focal_x?: number
    focal_y?: number
    focal_scale?: number
    is_primary?: boolean
}

const props = defineProps<{ images: ImageEntry[] }>()

const activeIndex = ref(0)
const lightboxOpen = ref(false)
const lightboxRef = ref<HTMLElement | null>(null)

const MIN_SCALE = 1
const MAX_SCALE = 4
const zoomScale = ref(1)
const panX = ref(0)
const panY = ref(0)
const isDragging = ref(false)
let dragStart = { x: 0, y: 0 }
let panStart = { x: 0, y: 0 }
let pinchStartDist = 0
let pinchStartScale = 1

function clamp(v: number, min: number, max: number) {
    return Math.min(max, Math.max(min, v))
}

function resetZoom() {
    zoomScale.value = 1
    panX.value = 0
    panY.value = 0
}

function clampPan() {
    const rect = lightboxRef.value?.getBoundingClientRect()
    const slack = 120
    const maxX = ((zoomScale.value - 1) / 2) * (rect?.width ?? 0) + slack
    const maxY = ((zoomScale.value - 1) / 2) * (rect?.height ?? 0) + slack
    panX.value = clamp(panX.value, -maxX, maxX)
    panY.value = clamp(panY.value, -maxY, maxY)
}

function zoomAt(clientX: number, clientY: number, targetScale: number) {
    const rect = lightboxRef.value?.getBoundingClientRect()
    if (!rect) return
    const newScale = clamp(targetScale, MIN_SCALE, MAX_SCALE)
    if (newScale === zoomScale.value) return
    const cx = clientX - rect.left - rect.width / 2
    const cy = clientY - rect.top - rect.height / 2
    const ratio = newScale / zoomScale.value
    panX.value = (panX.value - cx) * ratio + cx
    panY.value = (panY.value - cy) * ratio + cy
    zoomScale.value = newScale
    if (newScale === MIN_SCALE) { panX.value = 0; panY.value = 0 }
    clampPan()
}

function zoomBy(delta: number) {
    const rect = lightboxRef.value?.getBoundingClientRect()
    if (!rect) return
    zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, zoomScale.value + delta)
}

function onWheel(e: WheelEvent) {
    e.preventDefault()
    zoomAt(e.clientX, e.clientY, zoomScale.value + (e.deltaY < 0 ? 0.35 : -0.35))
}

function onDblClick(e: MouseEvent) {
    if (zoomScale.value > 1) resetZoom()
    else zoomAt(e.clientX, e.clientY, 2.5)
}

function onPointerDown(e: PointerEvent) {
    if (zoomScale.value <= 1) return
    isDragging.value = true
    dragStart = { x: e.clientX, y: e.clientY }
    panStart = { x: panX.value, y: panY.value }
    ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
}

function onPointerMove(e: PointerEvent) {
    if (!isDragging.value) return
    panX.value = panStart.x + (e.clientX - dragStart.x)
    panY.value = panStart.y + (e.clientY - dragStart.y)
    clampPan()
}

function onPointerUp() {
    isDragging.value = false
}

function touchDist(a: Touch, b: Touch) {
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
}

function onTouchStart(e: TouchEvent) {
    if (e.touches.length === 2) {
        pinchStartDist = touchDist(e.touches[0], e.touches[1])
        pinchStartScale = zoomScale.value
    }
}

function onTouchMove(e: TouchEvent) {
    if (e.touches.length === 2 && pinchStartDist > 0) {
        e.preventDefault()
        const d = touchDist(e.touches[0], e.touches[1])
        const mid = { x: (e.touches[0].clientX + e.touches[1].clientX) / 2, y: (e.touches[0].clientY + e.touches[1].clientY) / 2 }
        zoomAt(mid.x, mid.y, pinchStartScale * (d / pinchStartDist))
    }
}

function open() {
    if (!props.images.length) return
    lightboxOpen.value = true
}

function close() {
    lightboxOpen.value = false
}

function next() {
    if (!props.images.length) return
    resetZoom()
    activeIndex.value = (activeIndex.value + 1) % props.images.length
}

function prev() {
    if (!props.images.length) return
    resetZoom()
    activeIndex.value = (activeIndex.value - 1 + props.images.length) % props.images.length
}

function selectThumb(i: number) {
    resetZoom()
    activeIndex.value = i
}

function onKey(e: KeyboardEvent) {
    if (!lightboxOpen.value) return
    if (e.key === 'Escape') close()
    else if (e.key === 'ArrowRight') next()
    else if (e.key === 'ArrowLeft') prev()
    else if (e.key === '+' || e.key === '=') zoomBy(0.35)
    else if (e.key === '-') zoomBy(-0.35)
}

watch(lightboxOpen, (v) => {
    if (typeof document === 'undefined') return
    document.body.style.overflow = v ? 'hidden' : ''
    if (!v) resetZoom()
})

onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKey)
    if (typeof document !== 'undefined') document.body.style.overflow = ''
})
</script>

<template>
    <div class="space-y-3">
        <button
            v-if="images.length"
            type="button"
            class="block w-full cursor-zoom-in"
            @click="open"
        >
            <FocalImage
                :src="images[activeIndex].large"
                :alt="images[activeIndex].name"
                aspect="aspect-[4/3]"
                rounded="rounded-lg"
                fit="contain"
                :eager="true"
            />
        </button>
        <div v-else class="flex aspect-[4/3] items-center justify-center rounded-lg bg-gray-100 text-gray-400 dark:bg-gray-800">
            {{ $t('Keine Bilder hochgeladen.') }}
        </div>

        <div v-if="images.length > 1" class="grid grid-cols-4 gap-2 sm:grid-cols-6">
            <button
                v-for="(img, i) in images"
                :key="img.id"
                @click="selectThumb(i)"
                :class="[
                    'overflow-hidden rounded border-2 transition',
                    i === activeIndex ? 'border-brand-600' : 'border-transparent hover:border-gray-300',
                ]"
            >
                <FocalImage
                    :src="img.thumb"
                    :alt="img.name"
                    aspect="aspect-square"
                    fit="contain"
                />
            </button>
        </div>

        <Teleport to="body">
            <transition
                enter-active-class="transition ease-out duration-150"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition ease-in duration-100"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    v-if="lightboxOpen && images.length"
                    ref="lightboxRef"
                    class="fixed inset-0 z-[110] flex items-center justify-center overflow-hidden bg-black/95 p-4"
                    style="touch-action: none;"
                    @click.self="close"
                    @wheel="onWheel"
                    @touchstart="onTouchStart"
                    @touchmove="onTouchMove"
                >
                    <button
                        type="button"
                        @click="close"
                        class="absolute right-4 top-4 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                        :aria-label="$t('Close')"
                    >
                        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>

                    <div class="absolute left-4 top-4 z-20 flex items-center gap-1 rounded-full bg-white/10 p-1">
                        <button
                            type="button"
                            @click.stop="zoomBy(-0.35)"
                            class="inline-flex h-8 w-8 items-center justify-center rounded-full text-white hover:bg-white/20 disabled:opacity-30"
                            :disabled="zoomScale <= MIN_SCALE"
                            :aria-label="$t('Verkleinern')"
                        >
                            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14"/></svg>
                        </button>
                        <span class="min-w-[3rem] text-center text-xs text-white/80">{{ Math.round(zoomScale * 100) }}%</span>
                        <button
                            type="button"
                            @click.stop="zoomBy(0.35)"
                            class="inline-flex h-8 w-8 items-center justify-center rounded-full text-white hover:bg-white/20 disabled:opacity-30"
                            :disabled="zoomScale >= MAX_SCALE"
                            :aria-label="$t('Vergrößern')"
                        >
                            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
                        </button>
                        <button
                            v-if="zoomScale > MIN_SCALE"
                            type="button"
                            @click.stop="resetZoom"
                            class="inline-flex h-8 w-8 items-center justify-center rounded-full text-white hover:bg-white/20"
                            :aria-label="$t('Zoom zurücksetzen')"
                        >
                            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 1 0 9-9"/><path d="M3 4v5h5"/></svg>
                        </button>
                    </div>

                    <button
                        v-if="images.length > 1"
                        type="button"
                        @click.stop="prev"
                        class="absolute left-2 top-1/2 z-10 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 sm:left-4"
                        :aria-label="$t('Vorheriges Bild')"
                    >
                        <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>

                    <button
                        v-if="images.length > 1"
                        type="button"
                        @click.stop="next"
                        class="absolute right-2 top-1/2 z-10 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 sm:right-4"
                        :aria-label="$t('Nächstes Bild')"
                    >
                        <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"/></svg>
                    </button>

                    <img
                        :src="images[activeIndex].original || images[activeIndex].large"
                        :alt="images[activeIndex].name"
                        draggable="false"
                        class="max-h-full max-w-full select-none object-contain"
                        :class="zoomScale > MIN_SCALE ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-zoom-in'"
                        :style="{
                            transform: `translate(${panX}px, ${panY}px) scale(${zoomScale})`,
                            transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                        }"
                        style="touch-action: none;"
                        @click.stop
                        @dblclick.stop="onDblClick"
                        @pointerdown="onPointerDown"
                        @pointermove="onPointerMove"
                        @pointerup="onPointerUp"
                        @pointercancel="onPointerUp"
                    />

                    <div v-if="images.length > 1" class="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs text-white">
                        {{ activeIndex + 1 }} / {{ images.length }}
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>
